import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import CalendlyButton from "@/components/CalendlyButton";
import useSEO, { canonicalFor } from "@/lib/useSEO";

export default function ToolShell({ id, path, children }) {
  const { t } = useTranslation();
  useSEO({
    title: `${t(`tools.${id}.title`)} — Corpora Consultants`,
    description: t(`tools.${id}.lead`),
    canonical: canonicalFor(path),
    image: canonicalFor("/brand/corpora-og.jpg"),
  });
  return (
    <div data-testid={`tool-page-${id}`} className="pt-36 md:pt-44">
      <section className="max-w-[1480px] mx-auto px-6 md:px-10">
        <Link to="/tools" className="btn-ghost mb-10" data-testid="tool-back-link">
          <ArrowLeft size={14} className="rtl:rotate-180" /> {t("tools.backToTools")}
        </Link>
        <div className="overline mb-6">{t("tools.freeTools")} · {t("tools.takes2")}</div>
        <h1 className="display text-4xl sm:text-5xl lg:text-6xl text-[var(--bone)] max-w-4xl" data-testid="tool-title">
          {t(`tools.${id}.title`)}
        </h1>
        <p className="mt-6 text-[var(--bone-2)] text-base md:text-lg max-w-2xl leading-relaxed">{t(`tools.${id}.lead`)}</p>
      </section>
      <section className="max-w-[1480px] mx-auto px-6 md:px-10 mt-16">{children}</section>
      <section className="max-w-[1480px] mx-auto px-6 md:px-10 mt-24">
        <div className="border border-[var(--line-2)] p-8 md:p-12 grid gap-8 md:grid-cols-12 items-end" style={{ background: "var(--ink-2)" }}>
          <div className="md:col-span-8">
            <h2 className="display text-3xl md:text-4xl text-[var(--bone)]">{t("tools.ctaTitle")}</h2>
            <p className="mt-4 text-[var(--mute)] max-w-xl leading-relaxed">{t("tools.ctaBody")}</p>
            <p className="mt-6 text-xs text-[var(--mute-2)] max-w-xl">{t("tools.indicative")}</p>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <CalendlyButton className="btn-primary" data-testid="tool-book-consultation">
              {t("tools.book")} <ArrowRight size={14} className="rtl:rotate-180" />
            </CalendlyButton>
          </div>
        </div>
      </section>
    </div>
  );
}

export function Panel({ children, className = "", ...rest }) {
  return (
    <div className={`border border-[var(--line)] p-6 md:p-10 ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function FieldLabel({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute)] mb-3">
      {children}
    </label>
  );
}

export function Choice({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      className={`text-start px-4 py-3 border transition-colors duration-300 ${
        active ? "border-[var(--pewter)] text-[var(--bone)] bg-[var(--ink-2)]" : "border-[var(--line-2)] text-[var(--bone-2)] hover:border-[var(--bone-2)]"
      }`}
    >
      {children}
    </button>
  );
}
